/* eslint-disable no-console */
import { CHATKIT_CONFIGS } from './widget/configs';
import { extractContextFromDataset } from './widget/helpers';
import { loadChatkitScript } from './widget/chatkitScriptLoader';
import { fetchToken } from './widget/tokenService';
import { subscribeLiveAgentChannel, unsubscribeLiveAgentChannel } from './widget/liveAgentChannel';
import { processWebsocketContent, appendEvent } from './widget/liveAgentEvents';

window.ChatKitWidget = {
  widgets: new Map(),

  setupToggleWidget() {
    const container = document.getElementById('chatkit-widget-container');
    if (!container) return;

    const toggle = container.querySelector('#chatkit-toggle');
    const close = container.querySelector('#chatkit-close');

    this.boundToggleHandler = () => this._toggleWidget();

    if (toggle) {
      toggle.addEventListener('click', this.boundToggleHandler);
    }

    if (close) {
      close.addEventListener('click', this.boundToggleHandler);
    }
  },

  async initializeWidget(container) {
    await loadChatkitScript();

    const kit = container.querySelector('openai-chatkit');
    if (!kit) return;

    const widgetId = container.id;
    if (this.widgets.has(widgetId) && this.widgets.get(widgetId).isInitialized) {
      return;
    }

    if (kit.shadowRoot && kit.shadowRoot.querySelector('iframe')) {
      this.widgets.set(widgetId, { isInitialized: true });
      return;
    }

    const result = await this._prepareConfigAndToken(container);
    const { config, tokenData } = result;

    try {
      const userSettings = document.querySelector('[data-controller="user-account-settings"]');
      const appearanceMode = userSettings?.dataset?.userAccountSettingsThemeValue || 'light';

      kit.setOptions({
        api: {
          async getClientSecret(existing) {
            if (existing) {
              const newTokenData = await fetchToken(config.context);
              if (newTokenData) return newTokenData.token;
              throw new Error('Failed to refresh ChatKit token');
            }
            return tokenData.token;
          }
        },
        theme: {
          colorScheme: appearanceMode
        },
        composer: {
          placeholder: config.placeholder,
          attachments: config.attachments
        },
        startScreen: {
          greeting: config.greeting,
          prompts: config.prompts
        }
      });

      this.boundResponseEndHandler = () => this._onResponseEnd();
      kit.addEventListener('chatkit.response.end', this.boundResponseEndHandler);

      this.widgets.set(widgetId, { isInitialized: true, config });
    } catch (error) {
      console.error('Error initializing ChatKit:', error);
    }
  },

  _onResponseEnd() {
    if (this.liveAgentRequest) {
      setTimeout(() => this._switchToLiveAgent(), 3000);
    }
  },

  _switchToLiveAgent() {
    this.destroyAll();

    const container = document.getElementById('chatkit-widget-container');
    if (!container) return;

    const openaiChatkit = container.querySelector('openai-chatkit');
    const awsChatkit = container.querySelector('aws-chatkit');
    openaiChatkit.classList.add('d-none');
    awsChatkit.classList.remove('d-none');
    this._subscribeToLiveAgentWebSocket(awsChatkit);
  },

  _subscribeToLiveAgentWebSocket(container) {
    appendEvent(container, 'Connecting to a live agent...');

    const { websocket_url: websocketUrl } = this.liveAgentRequest;
    if (!websocketUrl) return;

    const subscribeMessage = JSON.stringify({ topic: 'aws/subscribe', content: { topics: ['aws/chat'] } });
    const ws = new WebSocket(websocketUrl);

    ws.onopen = () => ws.send(subscribeMessage);

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.topic !== 'aws/chat') return;

      const content = JSON.parse(msg.content);
      processWebsocketContent(container, content);
    };

    this.liveAgentWs = ws;
  },

  _getConfig(container) {
    const type = container.dataset.chatkitType || this.flowType;
    const config = { ...CHATKIT_CONFIGS[type] };

    const dynamicContext = extractContextFromDataset(container);
    if (Object.keys(dynamicContext).length > 0) {
      config.context = { ...config.context, ...dynamicContext };
    }

    return config;
  },

  async _prepareConfigAndToken(container) {
    if (container.dataset.chatkitType) {
      const config = this._getConfig(container);
      const tokenData = await fetchToken(config.context);
      return { config, tokenData };
    }

    const tokenData = await fetchToken({});
    if (!tokenData) return null;

    this.flowType = tokenData.flowType;
    const config = this._getConfig(container);
    return { config, tokenData };
  },

  async _toggleWidget() {
    const container = document.getElementById('chatkit-widget-container');
    if (!container) return;

    const win = container.querySelector('#chatkit-window');
    const toggle = container.querySelector('#chatkit-toggle');
    if (!win || !toggle) return;

    const isOpen = win.style.display === 'block';

    if (!isOpen) {
      await this.initializeWidget(container);
      win.style.display = 'block';
      toggle.style.display = 'none';
      subscribeLiveAgentChannel(container);
    } else {
      win.style.display = 'none';
      toggle.style.display = 'block';
      unsubscribeLiveAgentChannel();
    }
  },

  _destroyWidget(widgetId) {
    const container = document.getElementById(widgetId);
    if (!container) return;

    const kit = container.querySelector('openai-chatkit');
    if (kit) {
      if (this.boundResponseEndHandler) {
        kit.removeEventListener('chatkit.response.end', this.boundResponseEndHandler);
      }
      if (kit.shadowRoot) {
        kit.shadowRoot.innerHTML = '';
      }
    }

    if (widgetId === 'chatkit-widget-container' && this.boundToggleHandler) {
      const toggle = container.querySelector('#chatkit-toggle');
      const close = container.querySelector('#chatkit-close');
      if (toggle) toggle.removeEventListener('click', this.boundToggleHandler);
      if (close) close.removeEventListener('click', this.boundToggleHandler);
    }

    this.widgets.delete(widgetId);
  },

  destroyAll() {
    unsubscribeLiveAgentChannel();
    this.widgets.forEach((_, widgetId) => {
      this._destroyWidget(widgetId);
    });
  }
};
